import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  addSelectedRecall,
  getSelectedRecalls,
  removeSelectedRecall,
  clearSelectedRecalls,
  clearUnassignedSelectedRecalls,
  isRecallSelected,
  getSelectedRecallsGroupedByDealer,
  removeStagedManualStop
} from '@/lib/supabase/selectedRecalls';
import { stageManualStops } from '@/lib/supabase/manualStopContacts';
import type { ManualStopSubmission } from '@/lib/validation/manualStopValidation';
import { RecallData } from '@/types';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { logUserActivity } from '@/lib/activity/userActivity';
import {
  createDataLoadingError,
  createUserActionError,
  ErrorMetadata
} from '@/lib/utils/errorTranslator';

// Global Map to cache in-flight request promises across all hook instances
// This prevents duplicate calls even in React StrictMode by sharing the same promise
// Key is constant since data is now tenant-scoped (all users in tenant see same data)
const inFlightPromises = new Map<
  string,
  Promise<{ recalls: any[]; grouped: Record<string, any> }>
>();
const IN_FLIGHT_KEY = 'tenant-selected-recalls';

const useSelectedRecalls = () => {
  const { user } = useAuth();
  const [selectedRecalls, setSelectedRecalls] = useState<any[]>([]);
  const [groupedRecalls, setGroupedRecalls] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorMetadata, setErrorMetadata] = useState<ErrorMetadata | null>(null);

  const loadSelectedRecalls = useCallback(async () => {
    if (!user?.id) return;

    try {
      // Check if there's already an in-flight request (tenant-scoped)
      let dataPromise = inFlightPromises.get(IN_FLIGHT_KEY);

      if (!dataPromise) {
        // Only set loading state for new requests, not cached ones
        setLoading(true);
        setError(null);

        // Create new promise and cache it immediately
        dataPromise = (async () => {
          try {
            // API layer handles tenant isolation via RLS and getTenantId()
            const recalls = await getSelectedRecalls();
            const grouped = await getSelectedRecallsGroupedByDealer();
            return { recalls, grouped };
          } finally {
            // Clear cache after completion (success or error)
            inFlightPromises.delete(IN_FLIGHT_KEY);
          }
        })();

        inFlightPromises.set(IN_FLIGHT_KEY, dataPromise);
      }
      // If reusing cached promise, don't update loading state - just wait for result

      const { recalls, grouped } = await dataPromise;
      setSelectedRecalls(recalls);
      setGroupedRecalls(grouped);
    } catch (err) {
      const metadata = createDataLoadingError(err, 'loading selected recalls', loadSelectedRecalls);
      setErrorMetadata(metadata);
      setError(metadata.userMessage);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Load selected recalls on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadSelectedRecalls();
    } else {
      // Reset when user logs out
      setSelectedRecalls([]);
      setGroupedRecalls({});
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]); // Only depend on user?.id - loadSelectedRecalls is stable

  // Add a recall to selected recalls
  const addRecall = useCallback(
    async (recallData: RecallData) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      try {
        setError(null);

        // Check if recall is already selected by anyone in the tenant
        const alreadySelected = await isRecallSelected(recallData.vin, recallData.campaign_number);
        if (alreadySelected) {
          // Return early - don't throw error, just indicate it's already selected
          return selectedRecalls.find(
            r => r.vin === recallData.vin && r.campaign_number === recallData.campaign_number
          );
        }

        // user.id is still passed for audit trail (who added this recall)
        const newRecall = await addSelectedRecall(user.id, recallData);

        // Update local state
        setSelectedRecalls(prev => [...prev, newRecall]);

        // Reload grouped data (tenant-scoped)
        const grouped = await getSelectedRecallsGroupedByDealer();
        setGroupedRecalls(grouped);

        logUserActivity({
          category: 'recall_selection',
          action: 'add_to_assignment_list',
          entityType: 'recall',
          entityId: recallData.vin,
          entityName: `${recallData.vin} - ${recallData.campaign_number}`,
          metadata: { vin: recallData.vin, campaign_number: recallData.campaign_number }
        });

        return newRecall;
      } catch (err) {
        const metadata = createUserActionError(err, 'adding recall');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Remove a recall from selected recalls (any tenant user can remove)
  const removeRecall = useCallback(
    async (vin: string, campaignNumber: string) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      try {
        setError(null);

        // Tenant-scoped: no user.id needed
        await removeSelectedRecall(vin, campaignNumber);

        // Update local state
        setSelectedRecalls(prev =>
          prev.filter(recall => !(recall.vin === vin && recall.campaign_number === campaignNumber))
        );

        // Reload grouped data (tenant-scoped)
        const grouped = await getSelectedRecallsGroupedByDealer();
        setGroupedRecalls(grouped);

        logUserActivity({
          category: 'recall_selection',
          action: 'remove_from_assignment_list',
          entityType: 'recall',
          entityId: vin,
          entityName: `${vin} - ${campaignNumber}`,
          metadata: { vin, campaign_number: campaignNumber }
        });
      } catch (err) {
        const metadata = createUserActionError(err, 'removing recall');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Clear all selected recalls (tenant-scoped: clears for all tenant users)
  const clearAllRecalls = useCallback(async () => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);

      // Tenant-scoped: no user.id needed
      const summary = await clearSelectedRecalls();

      // Update local state
      setSelectedRecalls([]);
      setGroupedRecalls({});

      // Hand the caller the reconciliation so it can tell the user how many were kept
      // back and why. Previously this result was discarded, so a partial clear looked
      // identical to a complete one.
      return summary;
    } catch (err) {
      const metadata = createUserActionError(err, 'clearing recalls');
      setErrorMetadata(metadata);
      setError(metadata.userMessage);
      throw err;
    }
  }, [user?.id]);

  // Clear only unassigned recalls (tenant-scoped: clears for all tenant users)
  const clearUnassignedRecalls = useCallback(async () => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      setError(null);

      // Tenant-scoped: no user.id needed
      const result = await clearUnassignedSelectedRecalls();

      // Reload data to reflect changes
      await loadSelectedRecalls();

      return result;
    } catch (err) {
      const metadata = createUserActionError(err, 'clearing unassigned recalls');
      setErrorMetadata(metadata);
      setError(metadata.userMessage);
      throw err;
    }
  }, [user?.id, loadSelectedRecalls]);

  // Check if a recall is selected (tenant-scoped: checks if anyone in tenant selected it)
  const checkIfRecallSelected = useCallback(
    async (vin: string, campaignNumber: string): Promise<boolean> => {
      if (!user?.id) return false;

      try {
        // Tenant-scoped: no user.id needed
        return await isRecallSelected(vin, campaignNumber);
      } catch (err) {
        console.error('Error checking if recall is selected:', err);
        return false;
      }
    },
    [user?.id]
  );

  // Computed statistics
  const stats = useMemo(() => {
    const totalRecalls = selectedRecalls.length;
    const uniqueDealers = new Set(selectedRecalls.map(recall => recall.dealer_name)).size;
    const uniqueCampaigns = new Set(selectedRecalls.map(recall => recall.campaign_number)).size;

    // Group by campaign
    const campaignGroups = selectedRecalls.reduce(
      (acc, recall) => {
        const key = recall.campaign_number;
        if (!acc[key]) {
          acc[key] = {
            campaign_number: key,
            recall_title: recall.recall_title,
            count: 0,
            dealers: new Set()
          };
        }
        acc[key].count++;
        acc[key].dealers.add(recall.dealer_name);
        return acc;
      },
      {} as Record<string, any>
    );

    // Convert dealers sets to arrays
    Object.values(campaignGroups).forEach((group: any) => {
      group.dealers = Array.from(group.dealers);
    });

    // Skill level breakdown (placeholder - would need actual skill level data)
    const skillLevelBreakdown = {
      high: 0,
      medium: 0,
      low: 0,
      unset: totalRecalls // For now, assume all are unset
    };

    return {
      totalRecalls,
      uniqueDealers,
      uniqueCampaigns,
      campaignGroups: Object.values(campaignGroups),
      skillLevelBreakdown
    };
  }, [selectedRecalls]);

  // Check if user is authenticated
  const isAuthenticated = useMemo(() => {
    return !!user?.id;
  }, [user?.id]);

  // Filter to get only staged manual stops
  const stagedManualStops = useMemo(() => {
    return selectedRecalls.filter(r => r.is_manual_stop === true);
  }, [selectedRecalls]);

  // Filter to get only recalls (not manual stops)
  const recallsOnly = useMemo(() => {
    return selectedRecalls.filter(r => r.is_manual_stop !== true);
  }, [selectedRecalls]);

  // Stage a business or customer stop (one row per vehicle × campaign, created atomically)
  const addManualStop = useCallback(
    async (submission: ManualStopSubmission) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      try {
        setError(null);

        const newStops = await stageManualStops(submission);

        // Update local state
        setSelectedRecalls(prev => [...prev, ...newStops]);

        // Reload grouped data
        const grouped = await getSelectedRecallsGroupedByDealer();
        setGroupedRecalls(grouped);

        // Customer stops: no name or coordinates in the activity log (it isn't a PII store).
        const isCustomer = submission.stop_kind === 'customer';
        logUserActivity({
          category: 'recall_selection',
          action: 'add_to_assignment_list',
          entityType: 'manual_stop',
          entityId: (newStops[0]?.['id'] as string) || '',
          entityName: isCustomer
            ? `Customer stop · ${submission.location.city}`
            : submission.location.name,
          itemCount: newStops.length,
          metadata: {
            is_manual_stop: true,
            stop_kind: submission.stop_kind,
            reasons: submission.vehicles.map(v => v.manual_stop_reason),
            ...(!isCustomer && { lat: submission.location.lat, lng: submission.location.lng })
          }
        });

        return newStops;
      } catch (err) {
        const metadata = createUserActionError(err, 'adding manual stop');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        throw err;
      }
    },
    [user?.id]
  );

  // Remove a staged manual stop by ID
  const removeManualStop = useCallback(
    async (id: string) => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      try {
        setError(null);

        await removeStagedManualStop(id);

        // Update local state
        setSelectedRecalls(prev => prev.filter(r => r.id !== id));

        // Reload grouped data
        const grouped = await getSelectedRecallsGroupedByDealer();
        setGroupedRecalls(grouped);

        logUserActivity({
          category: 'recall_selection',
          action: 'remove_from_assignment_list',
          entityType: 'manual_stop',
          entityId: id,
          entityName: id,
          metadata: {
            is_manual_stop: true
          }
        });
      } catch (err) {
        const metadata = createUserActionError(err, 'removing manual stop');
        setErrorMetadata(metadata);
        setError(metadata.userMessage);
        throw err;
      }
    },
    [user?.id]
  );

  return {
    selectedRecalls,
    recallsOnly,
    groupedRecalls,
    loading,
    error, // Keep for backward compatibility
    errorMetadata, // New: error metadata for modern error display
    stats,
    isAuthenticated,
    addRecall,
    removeRecall,
    clearAllRecalls,
    clearUnassignedRecalls,
    checkIfRecallSelected,
    refreshData: loadSelectedRecalls,
    // Manual stop methods
    stagedManualStops,
    addManualStop,
    removeManualStop
  };
};

export default useSelectedRecalls;
